import { UserPlus } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Card } from "@/components/ui/card";

import { leadFormTypes } from "../contacts/leadModel";
import type { LeadSubmission } from "../types";

const LATEST_COUNT = 5;

/** Monday 00:00 of the current week, in local time. */
export const getStartOfWeek = (now: Date = new Date()) => {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return start;
};

/** Count of this week's submissions for one form type (server-side total, one row fetched). */
const FormTypeCount = ({
  formType,
  since,
}: {
  formType: string;
  since: string;
}) => {
  const { total } = useGetList<LeadSubmission>("lead_submissions", {
    pagination: { page: 1, perPage: 1 },
    filter: { "form_type@eq": formType, "submitted_at@gte": since },
  });
  if (!total) return null;
  return (
    <li className="flex justify-between text-sm">
      <span>{formType}</span>
      <span className="font-semibold">{total}</span>
    </li>
  );
};

/** Dashboard card: this week's website lead submissions per form type plus the latest few. */
export const NewLeads = () => {
  const translate = useTranslate();
  const since = getStartOfWeek().toISOString();
  const { data, total, isPending } = useGetList<LeadSubmission>(
    "lead_submissions",
    {
      pagination: { page: 1, perPage: LATEST_COUNT },
      sort: { field: "submitted_at", order: "DESC" },
      filter: { "submitted_at@gte": since },
    },
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <div className="mr-3 flex">
          <UserPlus className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("resources.lead_submissions.new_leads.title")}
        </h2>
        <Link to="/lead_submissions" className="ml-auto text-sm underline">
          {translate("resources.lead_submissions.new_leads.view_all")}
        </Link>
      </div>
      <Card className="p-4 gap-3">
        {isPending ? null : !total ? (
          <p className="text-sm">
            {translate("resources.lead_submissions.new_leads.empty")}
          </p>
        ) : (
          <>
            <ul className="flex flex-col gap-1" aria-label="counts">
              {leadFormTypes.map((formType) => (
                <FormTypeCount
                  key={formType}
                  formType={formType}
                  since={since}
                />
              ))}
            </ul>
            <ul className="flex flex-col gap-2 border-t pt-3">
              {data?.map((lead) => (
                <li key={lead.id} className="text-sm">
                  <Link to={`/contacts/${lead.contact_id}/show`}>
                    <div className="font-medium">
                      {[lead.contact_first_name, lead.contact_last_name]
                        .filter(Boolean)
                        .join(" ")}
                    </div>
                    <div className="text-muted-foreground">
                      {lead.form_type} -{" "}
                      {new Date(lead.submitted_at).toLocaleDateString()}
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
    </div>
  );
};
